import { useImpersonation } from "@/contexts/ImpersonationContext";
import { env } from "@/env";
import { useUser } from "@/hooks/useUser";
import { useBackendHealthService } from "@/hooks/useBackendHealthService";
import { useOfflineBanner } from "@/hooks/useOfflineBanner";
import { useRefreshAppBanner } from "@/hooks/useRefreshAppBanner";
import { cn } from "@/lib/utils";

const ENVIRONMENT_LABELS: Record<string, string> = {
  development: "Localhost",
  staging: "Staging",
  production: "",
};

const ENVIRONMENT_STRIPE_STYLE = {
  backgroundColor: "rgb(255, 255, 255)",
  backgroundImage: `repeating-linear-gradient(
    -45deg,
    rgba(250, 204, 21, 0.1),
    rgba(250, 204, 21, 0.1) 8px,
    rgba(31, 41, 55, 0.1) 8px,
    rgba(31, 41, 55, 0.1) 16px
  )`,
};

const IMPERSONATION_STRIPE_STYLE = {
  backgroundColor: "rgb(255, 255, 255)",
  backgroundImage: `repeating-linear-gradient(
    -45deg,
    rgba(147, 197, 253, 0.1),
    rgba(147, 197, 253, 0.1) 8px,
    rgba(31, 41, 55, 0.1) 8px,
    rgba(31, 41, 55, 0.1) 16px
  )`,
};

const STATUS_BANNER_STRIPE_STYLE = {
  backgroundColor: "rgb(250, 204, 21)",
  backgroundImage: `repeating-linear-gradient(
    -45deg,
    rgba(0, 0, 0, 0.18),
    rgba(0, 0, 0, 0.18) 10px,
    transparent 10px,
    transparent 20px
  )`,
};

function TopBanner() {
  const { organization } = useImpersonation();
  const { canSeeImpersonation } = useUser();
  const environment = env.VITE_APP_ENVIRONMENT;
  const environmentLabel = ENVIRONMENT_LABELS[environment];
  const { isVisible: isOfflineBannerVisible } = useOfflineBanner();
  const { isVisible: isRefreshAppBannerVisible, refresh } =
    useRefreshAppBanner();
  const { isBackendHealthy } = useBackendHealthService();

  const showImpersonation = canSeeImpersonation && organization !== null;
  const showEnvironment =
    environmentLabel !== "" && environmentLabel !== undefined;
  const showOfflineBanner =
    isOfflineBannerVisible && isBackendHealthy !== true;
  const showTechnicalWorks =
    !showOfflineBanner && isBackendHealthy === false;

  if (
    !showImpersonation &&
    !showEnvironment &&
    !showOfflineBanner &&
    !isRefreshAppBannerVisible &&
    !showTechnicalWorks
  ) {
    return null;
  }

  return (
    <div className="flex w-full flex-col">
      {showOfflineBanner && (
        <div
          className={cn(
            "flex w-full items-center justify-center gap-x-2 px-4 py-2 text-center text-sm font-semibold",
            "text-gray-950",
          )}
          style={STATUS_BANNER_STRIPE_STYLE}
        >
          <span>📡 Du bist offline. Bitte prüfe deine Internetverbindung.</span>
        </div>
      )}
      {isRefreshAppBannerVisible && (
        <div
          className={cn(
            "flex w-full items-center justify-center gap-x-3 px-4 py-2 text-center text-sm font-semibold",
            "text-gray-950",
          )}
          style={STATUS_BANNER_STRIPE_STYLE}
        >
          <span>✨ Eine neue Version ist verfügbar.</span>
          <button className="underline" onClick={refresh} type="button">
            App aktualisieren
          </button>
        </div>
      )}
      {showTechnicalWorks && (
        <div
          className={cn(
            "flex w-full items-center justify-center gap-x-2 px-4 py-2 text-center text-sm font-semibold",
            "text-gray-950",
          )}
          style={STATUS_BANNER_STRIPE_STYLE}
        >
          <span>
            🛠️ Es gibt gerade technische Arbeiten. Bitte versuche es gleich
            erneut.
          </span>
        </div>
      )}
      {showEnvironment && (
        <div
          className={cn(
            "flex w-full items-center justify-center gap-x-2 px-4 py-2 text-center text-sm font-semibold",
            "text-gray-900",
          )}
          style={ENVIRONMENT_STRIPE_STYLE}
        >
          <span>Umgebung: {environmentLabel}</span>
        </div>
      )}
      {showImpersonation && (
        <div
          className={cn(
            "flex w-full items-center justify-center gap-x-2 px-4 py-2 text-center text-sm font-semibold",
            "text-gray-900",
          )}
          style={IMPERSONATION_STRIPE_STYLE}
        >
          <span>
            Impersonation als Organisation <strong>{organization}</strong> ist
            aktiv
          </span>
        </div>
      )}
    </div>
  );
}

export { TopBanner };
